import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Building2, UserSquare2, CalendarCheck2, CheckSquare, AlertCircle } from 'lucide-react';
import { req } from '../services/api';


/**
 * Panel lateral con contexto comercial del destinatario de un correo.
 * Se usa en el composer y en el modal de "Sugerir respuestas".
 *
 * Props:
 *   - email: destinatario (required)
 *   - prefilled: ctx ya cargado (opcional, para evitar nueva petición)
 *   - compact: bool para versión reducida
 */
export default function ContextoComposer({ email, prefilled, compact = false }) {
  const [ctx, setCtx] = useState(prefilled || null);
  const [loading, setLoading] = useState(!prefilled);

  useEffect(() => {
    if (prefilled) { setCtx(prefilled); setLoading(false); return; }
    if (!email || !email.includes('@')) { setCtx(null); setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    req(`/api/correo/ai/contexto?email=${encodeURIComponent(email)}`)
      .then((r) => { if (!cancel) setCtx(r); })
      .catch(() => { if (!cancel) setCtx(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [email, prefilled]);

  if (loading) {
    return (
      <Card className="p-3 bg-ink-50/40 border-dashed text-xs text-ink-500" data-testid="contexto-loading">
        <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Cargando contexto…
      </Card>
    );
  }

  if (!ctx || !ctx.vinculado) {
    return (
      <Card className="p-3 bg-ink-50/40 border-dashed text-xs text-ink-500" data-testid="contexto-sin-vinculo">
        <AlertCircle className="w-3 h-3 inline mr-1.5" /> Este destinatario no está vinculado a ninguna empresa o contacto del CRM.
      </Card>
    );
  }

  return (
    <Card className="p-3 border-violet-200 bg-violet-50/30" data-testid="contexto-comercial">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-2 flex items-center gap-1.5">
        <Building2 className="w-3 h-3" /> Contexto comercial
      </div>

      <div className="space-y-2 text-xs">
        {ctx.entidad && (
          <div className="flex items-start gap-2">
            <Building2 className="w-3.5 h-3.5 text-ink-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-ink-900 truncate">{ctx.entidad.nombre}</div>
              <div className="text-ink-500 text-[11px]">
                {[ctx.entidad.tipo, ctx.entidad.provincia, ctx.entidad.ccaa].filter(Boolean).join(' · ')}
              </div>
              {ctx.entidad.estado_comercial && (
                <Badge className="bg-violet-100 text-violet-700 border-violet-200 border text-[9px] mt-0.5">
                  {ctx.entidad.estado_comercial}
                </Badge>
              )}
            </div>
          </div>
        )}

        {ctx.contacto && (
          <div className="flex items-start gap-2">
            <UserSquare2 className="w-3.5 h-3.5 text-ink-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-ink-900">
                {[ctx.contacto.nombre, ctx.contacto.apellidos].filter(Boolean).join(' ')}
                {ctx.contacto.cargo && <span className="text-ink-500"> — {ctx.contacto.cargo}</span>}
              </div>
              {ctx.contacto.telefono && <div className="text-ink-500 text-[11px]">📞 {ctx.contacto.telefono}</div>}
            </div>
          </div>
        )}

        {!compact && ctx.visitas_recientes && ctx.visitas_recientes.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 text-ink-700 font-semibold mt-2 mb-1">
              <CalendarCheck2 className="w-3.5 h-3.5" /> Últimas visitas
            </div>
            <div className="space-y-1 ml-4">
              {ctx.visitas_recientes.map((v, i) => (
                <div key={i} className="text-ink-600" data-testid={`contexto-visita-${i}`}>
                  · <span className="font-medium">{v.fecha}</span> ({v.tipo}): <span className="line-clamp-2">{v.resumen || '(sin resumen)'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!compact && ctx.tareas_pendientes && ctx.tareas_pendientes.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 text-ink-700 font-semibold mt-2 mb-1">
              <CheckSquare className="w-3.5 h-3.5" /> Tareas pendientes
            </div>
            <div className="space-y-1 ml-4">
              {ctx.tareas_pendientes.map((t, i) => (
                <div key={i} className="text-ink-600" data-testid={`contexto-tarea-${i}`}>
                  · <Badge className={`text-[9px] border ${
                    t.prioridad === 'alta' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    t.prioridad === 'baja' ? 'bg-ink-50 text-ink-600 border-ink-200' :
                    'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>{t.prioridad}</Badge>{' '}
                  <span className="font-medium">{t.titulo}</span>
                  {t.vencimiento && <span className="text-ink-400"> · vto {t.vencimiento}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
